/**
 * `/settings/delivery-flow` view model (#2501): the board preview rows and
 * the actions chain. Pure functions only, pinned by
 * `pages/__tests__/deliveryFlowModel.test.ts`.
 */
import type { DeliveryFlow } from './deliveryFlowShared';
import { addCalendarDays } from '@lib/calendar-day';

type Column = DeliveryFlow['columns'][number];
type Action = DeliveryFlow['actions'][number];

export interface PreviewRow {
  date: string;
  rest: boolean;
}

export type ChainItem = { kind: 'state'; stage: Column } | { kind: 'action'; action: Action };

/**
 * The backend rule word for word (`delivery-flow.ts` `isRestDay`): ISO
 * weekday, 1 = Monday ... 7 = Sunday.
 */
function isRestDay(date: string, restWeekdays: readonly number[]): boolean {
  const day = new Date(`${date}T00:00:00Z`).getUTCDay();
  return restWeekdays.includes(day === 0 ? 7 : day);
}

/** One row per day of the board range, from `today` (merchant-local YYYY-MM-DD). */
export function previewRows(
  today: string,
  rangeDays: number,
  restWeekdays: readonly number[],
): PreviewRow[] {
  return Array.from({ length: rangeDays }, (_, offset) => {
    const date = addCalendarDays(today, offset);
    return { date, rest: isRestDay(date, restWeekdays) };
  });
}

/**
 * State → action → state ... in the order of `actions`. A state box is drawn
 * once where one action's `to` is the next action's `from`; otherwise each
 * action gets its own `from` box.
 */
export function actionChain(actions: readonly Action[]): ChainItem[] {
  const chain: ChainItem[] = [];
  for (const action of actions) {
    const last = chain[chain.length - 1];
    if (last?.kind !== 'state' || last.stage !== action.from) {
      chain.push({ kind: 'state', stage: action.from });
    }
    chain.push({ kind: 'action', action }, { kind: 'state', stage: action.to });
  }
  return chain;
}
